// Shared by the interactive Crimes tab and the flat export/PDF variant, so the
// printed report and the dashboard describe a behaviour the same way.
//
// The payload is Quark Engine output. Its raw form is smali, which is why the
// old rendering (JSON.stringify / JsonView) was unreadable:
//
//   sequence:    [{ parent, call_graph }]  the APIs run one after another there
//   combination: [{ parent, call_graph }]  both APIs are present, order unproven
//   register:    [{ parent, reg_index }]   they share registers, so data flows
//   call_graph:  [{ api, path: [{ caller, invoke_at: [smali lines] }] }]
//
// All three lists are keyed by the same `parent` method, so they are merged
// into one entry per location instead of three lists repeating method names.

const asArray = (value) => (Array.isArray(value) ? value : []);

// "java/lang/String" -> "String". Inner classes keep their $ suffix, which is
// meaningful to a reader ("ForceStopRunnable$BroadcastReceiver").
const simpleName = (path) => {
  const text = String(path || "");
  const slash = text.lastIndexOf("/");
  return slash === -1 ? text : text.slice(slash + 1);
};

const PRIMITIVES = {
  V: "void",
  Z: "boolean",
  B: "byte",
  S: "short",
  C: "char",
  I: "int",
  J: "long",
  F: "float",
  D: "double",
};

// Reads one JVM type token at `i`, returns [readableType, nextIndex].
// Tolerates a missing closing ";" because the invoke_at targets drop it on the
// return type ("...loadClass(Ljava/lang/String;)Ljava/lang/Class").
const readType = (text, i) => {
  let arrays = 0;
  let index = i;

  while (text[index] === "[") {
    arrays += 1;
    index += 1;
  }

  const token = text[index];
  if (token === undefined) return [null, index];

  let base;
  if (token === "L") {
    const end = text.indexOf(";", index);
    base = simpleName(end === -1 ? text.slice(index + 1) : text.slice(index + 1, end));
    index = end === -1 ? text.length : end + 1;
  } else {
    base = PRIMITIVES[token] || token;
    index += 1;
  }

  return [base + "[]".repeat(arrays), index];
};

// "(Ljava/lang/String;ILandroid/net/Uri;)V" -> { params: [String, int, Uri], returns: "void" }
export const parseDescriptor = (descriptor) => {
  const text = String(descriptor || "");
  const open = text.indexOf("(");
  const close = text.indexOf(")");

  if (open === -1 || close === -1) return { params: [], returns: null };

  const params = [];
  let i = open + 1;

  while (i < close) {
    const [type, next] = readType(text, i);
    if (type === null || next === i) break;
    params.push(type);
    i = next;
  }

  const [returns] = readType(text, close + 1);
  return { params, returns };
};

// "Lim/f;->a ()Lim/e;" -> { simpleClass: "f", method: "a", params: [], returns: "e" }
const METHOD_REF = /^\s*L?([^;]+);?\s*->\s*([^\s(]+)\s*(\(.*)$/;

export const parseMethodRef = (ref) => {
  const match = METHOD_REF.exec(String(ref || ""));
  if (!match) return null;

  const [, classPath, method, descriptor] = match;
  const { params, returns } = parseDescriptor(descriptor);

  return { classPath, simpleClass: simpleName(classPath), method, params, returns };
};

const signatureOf = (simpleClass, method, params) =>
  method === "<init>"
    ? `new ${simpleClass}(${params.join(", ")})`
    : `${simpleClass}.${method}(${params.join(", ")})`;

// Never throws and never returns empty: an unrecognised shape falls back to the
// raw string, so an unexpected Quark version degrades instead of blanking out.
export const formatMethodRef = (ref) => {
  const parsed = parseMethodRef(ref);
  if (!parsed) return String(ref ?? "");

  return signatureOf(parsed.simpleClass, parsed.method, parsed.params);
};

// The matched_api entries are objects rather than a single string.
export const formatMatchedApi = (api) => {
  if (!api || typeof api !== "object") return String(api ?? "");

  const simpleClass = simpleName(String(api.class || "").replace(/^L/, "").replace(/;$/, ""));
  const { params, returns } = parseDescriptor(api.descriptor);
  const base = signatureOf(simpleClass, api.method, params);
  const withReturn = returns && returns !== "void" ? `${base} → ${returns}` : base;

  const keywords = asArray(api.match_keywords);
  return keywords.length ? `${withReturn}  ·  matches "${keywords.join('", "')}"` : withReturn;
};

// "invoke-virtual ['v1', 'v2'] Ljava/lang/ClassLoader.loadClass(Ljava/lang/String;)Ljava/lang/Class"
// The target here uses "." instead of "->" and drops the trailing ";".
const INVOKE_LINE = /^\s*invoke-(\S+)\s*\[([^\]]*)\]\s*(.*)$/;

export const parseInvokeLine = (line) => {
  const text = String(line || "");
  const match = INVOKE_LINE.exec(text);
  if (!match) return { kind: null, registers: [], target: text, raw: text };

  const [, kind, registerList, target] = match;
  const registers = (registerList.match(/v\d+/g) || []).slice();

  const open = target.indexOf("(");
  const head = open === -1 ? target : target.slice(0, open);
  const descriptor = open === -1 ? "" : target.slice(open);
  const dot = head.lastIndexOf(".");

  if (dot === -1) return { kind, registers, target, raw: text };

  const simpleClass = simpleName(head.slice(0, dot).replace(/^L/, ""));
  const { params } = parseDescriptor(descriptor);

  return { kind, registers, target: signatureOf(simpleClass, head.slice(dot + 1), params), raw: text };
};

// The same invocation is routinely repeated three to five times in one method;
// collapsing them is the single biggest volume reduction here.
export const dedupeInvokes = (lines) => {
  const counts = new Map();

  asArray(lines).forEach((line) => {
    const key = String(line);
    counts.set(key, (counts.get(key) || 0) + 1);
  });

  return [...counts.entries()].map(([raw, count]) => ({ ...parseInvokeLine(raw), count }));
};

// path[0] is the parent itself; the API is invoked in the last caller. One step
// means a direct call, more means it is reached through those methods.
export const buildCallChain = (path) => {
  const steps = asArray(path)
    .map((step) => step?.caller)
    .filter(Boolean);

  return { direct: steps.length <= 1, via: steps.slice(1).map(formatMethodRef) };
};

const strengthOf = (location) =>
  (location.ordered ? 4 : 0) + (location.registers.length ? 2 : 0) + (location.cooccurring ? 1 : 0);

// One entry per method, strongest evidence first.
export const mergeCrimeLocations = (crime) => {
  const byParent = new Map();

  const touch = (parent) => {
    if (!byParent.has(parent)) {
      byParent.set(parent, {
        parent,
        ordered: false,
        cooccurring: false,
        registers: [],
        callGraph: [],
      });
    }
    return byParent.get(parent);
  };

  // sequence first: when a method appears in both lists its ordered call graph
  // is the one worth showing.
  asArray(crime?.sequence).forEach((entry) => {
    if (!entry?.parent) return;
    const location = touch(entry.parent);
    location.ordered = true;
    if (!location.callGraph.length) location.callGraph = asArray(entry.call_graph);
  });

  asArray(crime?.combination).forEach((entry) => {
    if (!entry?.parent) return;
    const location = touch(entry.parent);
    location.cooccurring = true;
    if (!location.callGraph.length) location.callGraph = asArray(entry.call_graph);
  });

  asArray(crime?.register).forEach((entry) => {
    if (!entry?.parent) return;
    touch(entry.parent).registers = asArray(entry.reg_index);
  });

  return [...byParent.values()].sort((a, b) => strengthOf(b) - strengthOf(a));
};

// Plain English for the three field names — this is what actually makes the
// section readable.
export const evidenceLabels = (location) => {
  const labels = [];

  if (location.ordered) labels.push("Called in order");
  else if (location.cooccurring) labels.push("Both present");

  if (location.registers.length) {
    labels.push(`Shares data (${location.registers.map((index) => `v${index}`).join(", ")})`);
  }

  return labels;
};

export const EVIDENCE_LEGEND =
  '"Called in order" — the APIs run one after another in that method. ' +
  '"Shares data" — they use the same registers, so a value flows between them. ' +
  '"Both present" — both APIs appear, but the order could not be proven.';

// A crime is only worth expanding when it has APIs or locations to show.
export const crimeHasDetails = (crime) =>
  asArray(crime?.matched_api).length > 0 || mergeCrimeLocations(crime).length > 0;
